import { numberFormatter } from '~/utils/number'
import { reformat } from '~/utils/date'
import { MARKET_SLUG } from '~/src/constants/markets'
import { format, parseISO } from 'date-fns'
import priceCustomTooltip from '~/utils/chart/customTooltips/priceCustomTooltip'
import tailwindConfig from '~/tailwind.config'
import resolveConfig from 'tailwindcss/resolveConfig'

const DIGITS = 2

const fullConfig = resolveConfig(tailwindConfig)

export const getAxis = ({
  title,
  min,
  max,
  ticks,
  display = true,
  position = 'left',
  drawOnChartArea = true,
}: any) => ({
  display,
  min,
  max,
  position,
  grid: {
    drawOnChartArea,
    drawTicks: false,
  },
  ticks: {
    font: {
      family: 'Montserrat',
      color: fullConfig.theme.colors.s['900'],
    },
    beginAtZero: true,
    padding: 10,
    ...ticks,
  },
  title: {
    display: true,
    text: title,
    color: fullConfig.theme.colors.s['900'],
    font: {
      family: 'Montserrat',
      size: 12,
    },
  },
})

const getCategoryName = (dataset) => {
  const { category, feedstock } = dataset
  if (category) return category.name
  if (feedstock) return feedstock.name
  return ''
}

const getBaseLabel = (context) => {
  const { dataIndex, dataset } = context
  const { label, data, market, isSustainable, seriesTitle } = dataset
  const categoryName = getCategoryName(dataset)
  const labelReplaceFirst = label.replace(/#/, '')
  const { x: date } = data[dataIndex]
  const dateLabel = reformat(date, 'MMM dd, yyyy')
  const sustainableLabel = isSustainable ? 'Sustainable ' : ''

  if (market !== MARKET_SLUG.CATHODE)
    return `(${dateLabel}) ${categoryName}, ${sustainableLabel}${labelReplaceFirst}`
  return `(${dateLabel}) ${seriesTitle}, ${labelReplaceFirst}`
}

const label = (context) => {
  const { dataIndex, dataset } = context

  const { symbol, data, yAxisID } = dataset

  const { y: value } = data[dataIndex]
  const baseLabel = getBaseLabel(context)

  if (symbol) {
    if (symbol === '%') return `${baseLabel}: ${value}${symbol}`

    const formattedValue = numberFormatter(value, DIGITS)
    return `${baseLabel}: ${symbol}${formattedValue}`
  }

  if (yAxisID === 'y1') {
    return `${baseLabel}: $${numberFormatter(value, DIGITS)}`
  }

  return `${baseLabel}: ${value}%`
}

const labelColor = (context) => ({
  backgroundColor: context.dataset.borderColor,
  borderColor: context.dataset.borderColor,
  borderWidth: 2,
  borderRadius: 4,
  color: fullConfig.theme.colors.s['100'],
})

const title = (tooltipItems) => {
  const { dataIndex } = tooltipItems[0]
  const tooltipItem = tooltipItems[0].dataset.data[dataIndex]
  const unixEpochDates = tooltipItems.map((item) =>
    Date.parse(item.dataset.data[item.dataIndex].x),
  )
  const minDate = new Date(Math.min(...unixEpochDates))
    .toISOString()
    .split('T')[0]
  const maxDate = new Date(Math.max(...unixEpochDates))
    .toISOString()
    .split('T')[0]

  const date = tooltipItem.x

  if (maxDate !== minDate) {
    return `${reformat(
      minDate,
      'MMM dd, yyyy',
    )} to ${reformat(maxDate, 'MMM dd, yyyy')}`
  }

  try {
    return reformat(date, 'MMM dd, yyyy')
  } catch {
    return reformat(
      format(parseISO(date), 'yyyy-MM-dd HH:mm:ss'),
      'MMM dd, yyyy',
    )
  }
}

export const getChartOptions = ({ categories, replaceLegend, dashPattern }) => {
  return {
    responsive: true,
    maintainAspectRatio: false,
    hover: {
      intersect: false,
      mode: 'x',
    },

    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'month',
        },
        distribution: 'linear',
        bounds: 'ticks',
        grid: {
          drawOnChartArea: false,
        },
        stacked: false,
        ticks: {
          font: {
            family: 'Montserrat',
            size: 12,
            color: fullConfig.theme.colors.s['700'],
          },
        },
      },
    },

    plugins: {
      htmlLegend: {
        containerID: 'legend-container',
        categories,
        replaceLegend,
      },
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        enabled: false,
        external: priceCustomTooltip,
        mode: 'x',
        position: 'nearest',
        intersect: false,
        titleFont: {
          family: 'Montserrat',
          size: 14,
        },
        bodyFont: {
          family: 'Montserrat',
          size: 12,
        },
        multiKeyBackground: '#00000000',
        bodySpacing: 8,
        callbacks: {
          label,
          labelColor,
          title,
        },
      },
      crosshair: {
        line: {
          dashPattern,
          color: fullConfig.theme.colors.s['500'],
          width: 1,
        },
        zoom: {
          enabled: false,
        },
        snap: {
          enabled: true,
        },
        sync: {
          enabled: false,
        },
      },
    },
  }
}
